import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { TextField } from '@/form-control/fields'

import analytics from '@/services/analytics.service'
import { cn } from '@/utils/cn'

import Button from '../Button'

interface FilterInputProps {
    filters: any
    apply: () => void
    setFilter: (key: string, value: any) => void
    containerClass?: string
    inputClass?: string
    labelClass?: string
    inputLabel?: boolean | string
    iconClass?: string
    searchButton?: boolean
    searchButtonClass?: string
}

const FilterInput: React.FC<FilterInputProps> = ({
    filters,
    setFilter,
    apply,
    containerClass,
    inputClass,
    labelClass,
    inputLabel,
    iconClass,
    searchButton,
    searchButtonClass,
}) => {
    const [isFocused, setIsFocused] = useState(false)

    useEffect(() => {
        if (!searchButton && filters.freeText) {
            // Track search for auto-search (debounced)
            const searchTerm = filters.freeText.trim()
            if (searchTerm.length > 2) {
                // Only track meaningful searches
                setTimeout(() => {
                    analytics.trackResourceSearch(searchTerm, 0) // Results count will be updated elsewhere
                }, 300)
            }
            apply()
        }
    }, [filters.freeText])

    const handleSearch = () => {
        // Track manual search button clicks
        if (filters.freeText && filters.freeText.trim().length > 0) {
            analytics.trackResourceSearch(filters.freeText.trim(), 0)
        }
        apply()
    }

    return (
        <div className={cn('field-container', 'relative')}>
            {inputLabel && <label className={cn('field-label', labelClass)}>{inputLabel}</label>}

            <div
                className={cn(
                    'relative flex flex-row items-center gap-x-3 px-5 py-2',
                    'bg-white rounded-lg shadow-sm',
                    'border border-gray-300',
                    'transition-all duration-200',
                    isFocused && 'ring-2 ring-brand-500/20 border-brand-500/40 shadow-lg',
                    containerClass
                )}
            >
                <div className='flex items-center justify-center'>
                    <MagnifyingGlassIcon className={cn('w-5 h-5 text-gray-500', iconClass)} />
                </div>

                <input
                    type='text'
                    value={filters.freeText || ''}
                    onChange={(e) => setFilter('freeText', e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className={cn(
                        'flex-1 bg-transparent border-none outline-none focus:ring-0',
                        'text-gray-900 placeholder:text-gray-400',
                        'text-base font-medium',
                        inputClass
                    )}
                    placeholder='Search resources...'
                />

                {searchButton && (
                    <Button
                        onClick={handleSearch}
                        className={cn(
                            'px-4 py-1.5 bg-brand-600 hover:bg-brand-700',
                            'text-white text-sm font-medium',
                            'rounded-lg transition-colors',
                            searchButtonClass
                        )}
                    >
                        Search
                    </Button>
                )}
            </div>
        </div>
    )
}

export { FilterInput }
